import { IDisplateText, IGlobalText, IMbaText } from '@apps/flying/shared';
import { ValidatorSchema, Validator } from '@libs/utilities/validator';
import { EnumAiModel } from '@domains/ai-toolkit/shared';

import { FlyingAiTemplateSchema, IFlyingAiTemplate } from '../../interfaces';


/**------------------------------------------------------
 * Configurations
 */
export const FLYING_GENERATE_LISTING_LIMITS_OLD: IFlyingGenerateListingConfigOld = {
	FLY_ID_LENGTH		: 120,
	MAX_KEYWORDS		: 100,
	KEYWORD_LENGTH		: 120,
	TOPIC_LENGTH		: 250,
	INSTRUCTIONS_LENGTH	: 250,
	MAX_LIST_SIZE		: 500,
	GLOBAL_DEFAULTS		: {				// these are the defaults used to generate text length for global
		MAX_TITLE		: 120,
		MAX_DESCRIPTION : 500,
		MAX_KEYWORDS 	: 50
	},
	MBA_DEFAULTS		: {				// these are the defaults used to generate text length for mba
		MAX_BRAND		: 50,
		MAX_TITLE		: 60,
		MAX_BULLET1 	: 256,
		MAX_BULLET2 	: 256,
		MAX_DESCRIPTION : 500
	},
	AI_MODELS			: {
		VISION			: [EnumAiModel.Gpt4o, EnumAiModel.Gpt5, EnumAiModel.Gpt5Mini, EnumAiModel.Gpt5Nano, EnumAiModel.Gpt5_1],												// the order matter, it is the order in by which the default is selected for a user
		LISTING			: [EnumAiModel.Gpt4oMini, EnumAiModel.Gpt4o, EnumAiModel.Gpt5, EnumAiModel.Gpt5Mini, EnumAiModel.Gpt5Nano, EnumAiModel.Gpt5_1]	// the order matter, it is the order in by which the default is selected for a user
	}
};

interface IFlyingGenerateListingConfigOld {
	FLY_ID_LENGTH		: number;
	MAX_KEYWORDS		: number;
	KEYWORD_LENGTH		: number;
	TOPIC_LENGTH		: number;
	INSTRUCTIONS_LENGTH	: number;
	MAX_LIST_SIZE		: number;
	GLOBAL_DEFAULTS		: {				// these are the defaults used to generate text length for global
		MAX_TITLE		: number;
		MAX_DESCRIPTION : number;
		MAX_KEYWORDS 	: number;
	};
	MBA_DEFAULTS		: {				// these are the defaults used to generate text length for mba
		MAX_BRAND		: number;
		MAX_TITLE		: number;
		MAX_BULLET1 	: number;
		MAX_BULLET2 	: number;
		MAX_DESCRIPTION : number;
	};
	AI_MODELS			: {
		VISION			: EnumAiModel[];
		LISTING			: EnumAiModel[];
	};
}


/**------------------------------------------------------
 * Requests Vision
 */
export interface IFlyingGenerateByVisionRequestOld {
	model			: EnumAiModel;
	entries			: IFlyingGenerateByVisionParamsOld[];
}

export interface IFlyingGenerateByVisionParamsOld {
	flyId			: string;
	imageUrl		: string;
	topic			: string;
	instructions	: string;			// custom instructions
}


/**------------------------------------------------------
 * Request Keywords
 */
export interface IFlyingGenerateByKeywordsRequestOld {
	model			: EnumAiModel;
	entries			: IFlyingGenerateByKeywordsParamsOld[];
}

export interface IFlyingGenerateByKeywordsParamsOld {
	flyId			: string;
	mainKeywords	: string[];
	fillKeywords	: string[];
}


/**------------------------------------------------------
 * Request Template
 */
export interface IFlyingGenerateByTemplateRequestOld {
	model			: EnumAiModel;
	entries			: IFlyingGenerateByTemplateParamsOld[];
}

export interface IFlyingGenerateByTemplateParamsOld {
	flyId			: string;
	template		: IFlyingAiTemplate;
	instructions	: string;			// custom instructions
}


/**------------------------------------------------------
 * Response
 */
export interface IFlyingGeneratedListingResponseOld {
	flyId			: string;
	global 	 		: IGlobalText[]	| null;
	mba  	 		: IMbaText[]	| null;
	displate 		: IDisplateText	| null;
}


/**------------------------------------------------------
 * Schemas
 */
export class FlyingGenerateByVisionRequestSchemaOld implements ValidatorSchema<IFlyingGenerateByVisionRequestOld> {
	model	 	= Validator.Enum(EnumAiModel).isWhitelisted(FLYING_GENERATE_LISTING_LIMITS_OLD.AI_MODELS.VISION).isNotEmpty();
	entries		= Validator.Array().isNotEmpty().isMaxSize(FLYING_GENERATE_LISTING_LIMITS_OLD.MAX_LIST_SIZE).inspectEach<IFlyingGenerateByVisionParamsOld>({
		flyId			: Validator.String().isNotEmpty().isMaxLength(FLYING_GENERATE_LISTING_LIMITS_OLD.FLY_ID_LENGTH),
		imageUrl	: Validator.String().isNotEmpty(),
		topic			: Validator.String().isMaxLength(FLYING_GENERATE_LISTING_LIMITS_OLD.TOPIC_LENGTH).hasNoHtmlScripts(),
		instructions	: Validator.String().isMaxLength(FLYING_GENERATE_LISTING_LIMITS_OLD.INSTRUCTIONS_LENGTH).hasNoHtmlScripts()
	});
}

export class FlyingGenerateByKeywordsRequestSchemaOld implements ValidatorSchema<IFlyingGenerateByKeywordsRequestOld> {
	model 		= Validator.Enum(EnumAiModel).isWhitelisted(FLYING_GENERATE_LISTING_LIMITS_OLD.AI_MODELS.LISTING).isNotEmpty();
	entries		= Validator.Array().isNotEmpty().isMaxSize(FLYING_GENERATE_LISTING_LIMITS_OLD.MAX_LIST_SIZE).inspectEach<IFlyingGenerateByKeywordsParamsOld>({
		flyId		: Validator.String().isNotEmpty().isMaxLength(FLYING_GENERATE_LISTING_LIMITS_OLD.FLY_ID_LENGTH),
		mainKeywords: Validator.Array().isNotEmpty().isMaxSize(FLYING_GENERATE_LISTING_LIMITS_OLD.MAX_KEYWORDS)
						.each(Validator.String().isNotEmpty().isMaxLength(FLYING_GENERATE_LISTING_LIMITS_OLD.KEYWORD_LENGTH)
						.hasNoHtmlScripts().toLowerCase().trim()).isNotEmpty(),
		fillKeywords: Validator.Array().isMaxSize(FLYING_GENERATE_LISTING_LIMITS_OLD.MAX_KEYWORDS)
						.each(Validator.String().isNotEmpty().isMaxLength(FLYING_GENERATE_LISTING_LIMITS_OLD.KEYWORD_LENGTH)
						.hasNoHtmlScripts().toLowerCase().trim())
	});
}

export class FlyingGenerateByTemplateRequestSchemaOld implements ValidatorSchema<IFlyingGenerateByTemplateRequestOld> {
	model	 	= Validator.Enum(EnumAiModel).isWhitelisted(FLYING_GENERATE_LISTING_LIMITS_OLD.AI_MODELS.LISTING).isNotEmpty();
	entries		= Validator.Array().isNotEmpty().isMaxSize(FLYING_GENERATE_LISTING_LIMITS_OLD.MAX_LIST_SIZE).inspectEach<IFlyingGenerateByTemplateParamsOld>({
		flyId		: Validator.String().isNotEmpty().isMaxLength(FLYING_GENERATE_LISTING_LIMITS_OLD.FLY_ID_LENGTH),
		template	: Validator.Object().inspect<IFlyingAiTemplate>(FlyingAiTemplateSchema),
		instructions: Validator.String().isMaxLength(FLYING_GENERATE_LISTING_LIMITS_OLD.INSTRUCTIONS_LENGTH).hasNoHtmlScripts()
	});
}

export class FlyingGeneratedListingResponseSchemaOld implements ValidatorSchema<IFlyingGeneratedListingResponseOld> {
	flyId		= Validator.String().isNotEmpty();
	global 	 	= Validator.Array().optional();
	mba  	 	= Validator.Array().optional();
	displate 	= Validator.Object().optional();
}
